import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

const PRODUCT_NAME_PLACEHOLDER = '__PRODUCT_NAME__';

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * @typedef {object} SkinOnlyConfirmRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} alertBody
 * @property {HTMLTemplateElement} alertBodyTemplate
 * @property {HTMLButtonElement} confirmButton
 * @property {HTMLButtonElement} cancelButton
 * @property {HTMLButtonElement} closeButton
 */

/**
 * @extends {DialogComponent<SkinOnlyConfirmRefs>}
 */
class SkinOnlyConfirmDialog extends DialogComponent {
  /** @type {((value: boolean) => void) | null} */
  #resolvePromise = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClosed);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClosed);
  }

  #handleDialogClosed = () => {
    if (this.#resolvePromise) {
      this.#finish(false);
    }
  };

  /**
   * @param {{ productTitle: string }} options
   * @returns {Promise<boolean>}
   */
  requestConfirmation({ productTitle }) {
    this.#updateAlertBody(productTitle);
    return new Promise((resolve) => {
      this.#resolvePromise = resolve;
      this.showDialog();
    });
  }

  /**
   * @param {string} productTitle
   */
  #updateAlertBody(productTitle) {
    const { alertBody, alertBodyTemplate } = this.refs;
    if (!alertBody || !(alertBodyTemplate instanceof HTMLTemplateElement)) return;

    const markup = alertBodyTemplate.innerHTML.replaceAll(
      PRODUCT_NAME_PLACEHOLDER,
      escapeHtml(productTitle)
    );
    alertBody.innerHTML = markup;
  }

  #finish(confirmed) {
    this.#resolvePromise?.(confirmed);
    this.#resolvePromise = null;
  }

  handleConfirm = () => {
    this.#finish(true);
    this.closeDialog();
  };

  handleCancel = () => {
    this.#finish(false);
    this.closeDialog();
  };
}

if (!customElements.get('skin-only-confirm-dialog')) {
  customElements.define('skin-only-confirm-dialog', SkinOnlyConfirmDialog);
}

/**
 * @param {{ productTitle: string }} options
 * @returns {Promise<boolean>}
 */
export function requestSkinOnlyConfirmation({ productTitle }) {
  const dialog = document.getElementById('skin-only-confirm-dialog');
  if (dialog instanceof SkinOnlyConfirmDialog) {
    return dialog.requestConfirmation({ productTitle });
  }
  return Promise.resolve(true);
}
